// AnalyticsOperatorCallsPage.jsx
import { useEffect, useState } from 'react';
import {useLocation, useParams} from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import SortTable from '../../components/analytics/SortTable';
import {
  useDateTimeFilter,
  toBackendFormat,
  toInputFormat,
  getDefaultDateFrom,
  getDefaultDateTo,
} from '../../hooks/useDateTimeFilter';

const AnalyticsOperatorCallsPage = () => {
  const { projectId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { state } = useLocation();
  const project = state?.project;
  const dateTimeFilter = useDateTimeFilter('', '');
  const {
    filters: dateFilters,
    tempValues: tempDateValues,
    handleChange: handleDateChange,
    handleBlur: handleDateBlur,
  } = dateTimeFilter;

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        // Преобразуем даты в формат бэкенда
        const dateFrom = dateFilters.date_filter_from ? toBackendFormat(dateFilters.date_filter_from) : '';
        const dateTo = dateFilters.date_filter_to ? toBackendFormat(dateFilters.date_filter_to) : '';
        
        // Пример: GET /api/analytics/operators/123?date_filter_from=01-04-2025+14:30:00&date_filter_to=02-04-2025+15:30:00
        const res = await api.get(`/analytics/operators/${project.id}`, {
          params: {
            date_filter_from: dateFrom,
            date_filter_to: dateTo
          }
        });
        
        // Синхронизируем фильтры с сервером
        if (res.data && res.data.date_filter_from && res.data.date_filter_to) {
          let shouldUpdate = false;
          
          const serverFrom = res.data.date_filter_from || getDefaultDateFrom();
          const serverTo = res.data.date_filter_to || getDefaultDateTo();
          
          const inputFrom = toInputFormat(serverFrom);
          const inputTo = toInputFormat(serverTo);
          
          // Проверяем, нужно ли обновить фильтры
          if (inputFrom && inputFrom !== dateFilters.date_filter_from) {
            handleDateBlur({ target: { name: 'date_filter_from', value: inputFrom } });
            shouldUpdate = true;
          }
          if (inputTo && inputTo !== dateFilters.date_filter_to) {
            handleDateBlur({ target: { name: 'date_filter_to', value: inputTo } });
            shouldUpdate = true;
          }
          
          // Обновляем данные, если нужно
          if (shouldUpdate) {
            setData({ ...res.data, date_filter_from: inputFrom, date_filter_to: inputTo });
            return;
          }
        }
        
        setData(res.data);
      } catch (err) {
        console.error('Ошибка загрузки аналитики по операторам:', err);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [project.id, user, dateFilters.date_filter_from, dateFilters.date_filter_to]);


  if (!user) return <div>Доступ запрещён.</div>;
  if (loading) return <div className="main-content">Загрузка аналитики...</div>;
  if (!data) return <div className="main-content">Нет данных для отображения.</div>;
  // Подготовка данных для таблицы
  const prepareTableData = () => {
    if (!data || !data.operators) return [];
    
    const operators = [];
    
    for (const [name, stats] of Object.entries(data.operators)) {
      const row = {
        operator: name,
        total_calls: stats.total_calls,
        obscene: stats.obscene ? 'Да' : 'Нет'
      };
      
      // Добавляем данные по темам
      for (const [themeName, percent] of Object.entries(stats.theme_counts)) {
        row[themeName] = `${percent}%`;
      }
      
      operators.push(row);
    }
    
    return operators;
  };

  // Подготовка заголовков столбцов
  const prepareColumns = () => {
    if (!data) return [];
    
    // Используем явный порядок тем, если он передан с сервера
    const themeOrder = data.themes_order || Object.keys(data.theme_percent || {});
    const themes = themeOrder.filter(id => {
      const themeName = Object.keys(data.theme_percent || {}).find(name => data.themes[name] === id);
      return themeName && data.theme_percent[themeName] !== undefined;
    }).map(id => {
      return Object.keys(data.theme_percent || {}).find(name => data.themes[name] === id);
    }).filter(Boolean);
    
    return [
      { key: 'operator', label: 'Оператор' },
      ...themes.map(theme => ({ key: theme, label: theme })),
      { key: 'obscene', label: 'Нецензурная лексика' }
    ];
  };

  // Подготовка итоговой строки
  const prepareFooter = () => {
    if (!data) return null;
    
    const footer = {
      operator: 'Всего',
      total_calls: data.total_calls,
      obscene: ''
    };
    
    for (const [themeName, percent] of Object.entries(data.theme_percent)) {
      footer[themeName] = `${percent}%`;
    }
    
    return footer;
  };

  const tableData = prepareTableData();
  const columns = prepareColumns();
  const footerData = prepareFooter();

  return (
      <div className="main-content">
        <h2>Аналитика звонков операторов по темам — Проект {project.name_text}</h2>
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <label className="form-label">Дата от (с секундами)</label>
            <input 
              type="datetime-local" 
              name="date_filter_from"
              className="form-control form-control-sm" 
              value={tempDateValues.date_filter_from || dateFilters.date_filter_from}
              onChange={handleDateChange}
              onBlur={handleDateBlur}
              step="1"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Дата до (с секундами)</label>
            <input 
              type="datetime-local" 
              name="date_filter_to"
              className="form-control form-control-sm" 
              value={tempDateValues.date_filter_to || dateFilters.date_filter_to}
              onChange={handleDateChange}
              onBlur={handleDateBlur}
              step="1"
            />
          </div>
        </div>
        <SortTable
            columns={columns}
            data={tableData}
            footerData={footerData}
        />

        {/* Графики, таблицы, фильтры — по мере реализации */}
      </div>
  );
};

export default AnalyticsOperatorCallsPage;
